class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.cachedCounts = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.countAbortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    /* Bildschirmtastatur schliessen, sobald in den Ergebnissen gewischt wird — sie
       verdeckt auf dem Telefon sonst die untere Hälfte der Vorschau. */
    this.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: true });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.onViewportResize.bind(this));
    }

    const backButton = this.querySelector('[data-search-back]');
    if (backButton) backButton.addEventListener('click', () => this.close(true));

    /* Zurück-Geste und Zurück-Knopf des Browsers sollen im Overlay zuerst die Suche
       schliessen, statt die Seite zu verlassen — dafür liegt beim Öffnen ein
       History-Eintrag auf dem Stapel. */
    window.addEventListener('popstate', this.onPopState.bind(this));
  }

  /* Am Telefon ist die Vorschau ein Vollbild-Overlay, am Desktop ein Dropdown unter
     dem Suchfeld. Der Breakpoint entspricht dem in `component-predictive-search.css`. */
  get isOverlay() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  onPopState() {
    if (!this.isOpen) return;

    /* Der Eintrag ist durch die Navigation bereits vom Stapel — also nicht noch einmal
       `history.back()` auslösen, sonst verlässt der nächste Schritt die Seite. */
    this.historyEntryPushed = false;
    this.close(true);
  }

  openOverlay() {
    document.body.classList.add('overflow-hidden');

    if (this.historyEntryPushed) return;
    this.historyEntryPushed = true;
    history.pushState({ wbPredictiveSearch: true }, '');
  }

  closeOverlay() {
    document.body.classList.remove('overflow-hidden');
    this.input.blur();

    if (!this.historyEntryPushed) return;
    this.historyEntryPushed = false;
    history.back();
  }

  onTouchMove() {
    // Nur auf Touch-Geräten: an Maus-Rechnern gibt es keine Tastatur zu schliessen.
    if (!window.matchMedia('(pointer: coarse)').matches) return;
    if (document.activeElement !== this.input) return;

    /* Fokus auf den Ergebnis-Container (trägt `tabindex="-1"`) statt `this.input.blur()`:
       Die Tastatur verschwindet so oder so, weil kein Eingabefeld mehr aktiv ist — aber
       der Fokus bleibt INNERHALB der Komponente. Bei `blur()` läge er auf dem <body>,
       und `onFocusOut` würde die Vorschau schliessen, sobald das Suchfeld leer ist
       (Topseller-Startzustand). `preventScroll` verhindert, dass der Browser beim
       Fokuswechsel mitten in der Wischgeste an eine andere Stelle springt. */
    this.predictiveSearchResults.focus({ preventScroll: true });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      // Leeres Feld zeigt denselben Startzustand wie beim ersten Hineinklicken —
      // sonst faellt das Dropdown beim Loeschen des Suchbegriffs kommentarlos zu.
      this.showDefaultResults();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.showDefaultResults();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      const inputElement = this.getQuery(); // Adjust the selector as needed
      if (!this.contains(document.activeElement) && (!inputElement || !inputElement.value.trim())) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      // Escape soll schliessen; jede andere Taste, die das Feld leert, zeigt die Topseller.
      if (event.code === 'Escape') this.close(true);
      else this.showDefaultResults();
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null,
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  // Startzustand der Suchvorschau: Topseller, solange nichts getippt wurde.
  // Markup kommt aus `sections/wb-search-default.liquid` und wird pro Seitenaufruf einmal
  // geholt und danach aus dem Cache bedient. `location.pathname` als Basis, damit das
  // Sprachpraefix des Shops (de/en/fr/it) automatisch stimmt.
  showDefaultResults() {
    // Leerer String = Section liefert nichts (z. B. leere Kategorie). Dann nicht erneut fragen.
    if (this.defaultResultsMarkup === '') return;

    // `statusElement` wird sonst ausschliesslich in setLiveRegionLoadingState() gesetzt, das
    // nur im Lade-Pfad der echten Suche laeuft. Ohne diese Zeile bricht renderSearchResults()
    // hier mit "Cannot read properties of undefined" ab.
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');

    if (this.defaultResultsMarkup) {
      this.renderSearchResults(this.defaultResultsMarkup);
      return;
    }

    fetch(`${window.location.pathname}?section_id=wb-search-default`)
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((text) => {
        const markup =
          new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-wb-search-default')?.innerHTML.trim() ?? '';

        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.defaultResultsMarkup = markup;
        });

        // Nur anzeigen, wenn in der Zwischenzeit nicht doch getippt wurde
        if (markup && !this.getQuery().length) this.renderSearchResults(markup);
      })
      .catch(() => {
        // Der Startzustand ist eine Zugabe - schlaegt er fehl, bleibt das Dropdown einfach zu.
      });
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();
    this.getResultsCount(searchTerm);

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // limit_scope=each ist zwingend: ohne diesen Parameter gilt resources[limit] für ALLE
    // Ressourcen zusammen — dann teilen sich Produkte und Kategorien die Plätze und es
    // bleiben nur noch drei Produkte übrig. Angefordert werden 8 je Typ, damit nach dem
    // Filtern gleichnamiger Kategorien in der Section noch genug Chips übrig bleiben;
    // angezeigt werden 6 Produkte, 6 Kategorien und je 4 Vorschläge und Seiten.
    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search&resources%5Btype%5D=product,collection,query,page,article&resources%5Blimit%5D=8&resources%5Blimit_scope%5D=each`,
      {
        signal: this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  /**
   * Holt die Gesamtzahl der Treffer über templates/search.count.liquid.
   * Die Predictive-Search-API kennt nur die angezeigten Ergebnisse, nicht die Gesamtmenge.
   * Der Zähler ist Beiwerk: schlägt der Request fehl, bleibt der Abschluss-Button ohne Zahl.
   */
  getResultsCount(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();

    if (this.cachedCounts[queryKey] !== undefined) {
      this.renderResultsCount();
      return;
    }

    const searchUrl = this.input.form.getAttribute('action');
    if (!searchUrl) return;

    this.countAbortController.abort();
    this.countAbortController = new AbortController();

    fetch(`${searchUrl}?q=${encodeURIComponent(searchTerm)}&type=product&view=count`, {
      signal: this.countAbortController.signal,
    })
      .then((response) => (response.ok ? response.text() : ''))
      .then((text) => {
        // Cache in allen Instanzen synchron halten (Header und Suchseite teilen sich die Werte)
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedCounts[queryKey] = text.trim();
        });
        this.renderResultsCount();
      })
      .catch(() => {});
  }

  renderResultsCount() {
    const countElement = this.querySelector('[data-predictive-search-result-count]');
    if (!countElement) return;

    const count = this.cachedCounts[this.searchTerm.replace(' ', '-').toLowerCase()];

    countElement.textContent = count || '';
    countElement.hidden = !count;
  }

  /**
   * Die Ergebnisse kommen als fertiges Markup mit Bruttopreisen aus Liquid. Ohne diesen
   * Aufruf zeigt die Vorschau auch im Modus «Business» (zzgl. MwSt) Bruttopreise —
   * assets/facets.js macht nach dem Filtern dasselbe.
   */
  applyPriceMode() {
    if (typeof changePrice !== 'function' || typeof getStoredPriceOption !== 'function') return;

    changePrice(getStoredPriceOption());
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.applyPriceMode();
    this.renderResultsCount();

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  /* Der tatsächlich sichtbare Bereich, nicht der rechnerische. Auf iOS zählt
     `window.innerHeight` den Teil hinter Safaris Adressleiste mit — das Dropdown reichte
     darunter und die unterste Gruppe («Seiten») blieb unerreichbar. `visualViewport.height`
     berücksichtigt sowohl die Adressleiste als auch die eingeblendete Bildschirmtastatur. */
  getViewportHeight() {
    return window.visualViewport ? window.visualViewport.height : window.innerHeight;
  }

  getResultsMaxHeight() {
    const headerBottom = document.querySelector('.section-header').getBoundingClientRect().bottom;

    /* `Math.max(0, …)` ist zwingend: Der Header ist nicht sticky (`sticky_header_type:
       none`). Ist die Seite gescrollt, liegt er über dem Viewport und seine Unterkante
       ist negativ — aus der Subtraktion würde eine Addition. Gemessen bei 600 px
       Scrollposition: Unterkante −560, daraus 1286 px Höhe bei 726 px Bildschirm.
       Mobil trifft das regelmässig zu, weil iOS beim Fokussieren des Suchfelds die
       Seite scrollt. */
    this.resultsMaxHeight = this.getViewportHeight() - Math.max(0, headerBottom);
    return this.resultsMaxHeight;
  }

  /* Adressleiste ein-/ausblenden und Tastatur auf/zu ändern den sichtbaren Bereich, ohne
     dass ein normales `resize` feuert. Ohne diese Nachführung bliebe die beim Öffnen
     berechnete Höhe stehen — nach dem Wegblenden der Tastatur wäre das Dropdown dann
     unnötig kurz, bei erscheinender Tastatur zu lang. */
  onViewportResize() {
    if (!this.isOpen) return;

    /* Im Overlay bewusst nichts nachführen: Dort steht die Höhe über `100dvh` fest,
       damit beim Auf- und Zuklappen der Tastatur nichts springt. */
    if (this.isOverlay) return;

    this.resultsMaxHeight = false;
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
  }

  open() {
    if (this.isOverlay) {
      /* Im Overlay bestimmt das CSS-Raster die Höhe. Ein per JS gesetztes `max-height`
         würde die Ergebniszeile daran hindern, den verbleibenden Platz zu füllen. */
      this.predictiveSearchResults.removeAttribute('style');
      this.openOverlay();
    } else {
      /* Die Einheit muss hier stehen. `resultsMaxHeight` ist eine blanke Zahl; stand sie
         schon fest, wies der frühere Code `style.maxHeight = 849` zu — ein Längenwert
         ohne Einheit ist ungültiges CSS und wird stillschweigend verworfen. Das Dropdown
         hatte dann GAR KEINE Höhenbegrenzung, `overflow-y: auto` lief ins Leere und es
         wuchs weit über den Bildschirm hinaus, statt intern zu scrollen. */
      this.predictiveSearchResults.style.maxHeight = `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`;
    }

    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    const warOffen = this.isOpen;

    this.closeResults(clearSearchTerm);
    this.isOpen = false;

    /* Nur aufräumen, wenn tatsächlich etwas offen war — `close()` läuft auch aus
       Fokus- und Tastatur-Pfaden, in denen gar kein Overlay stand. */
    if (warOffen) this.closeOverlay();
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
